import { useState, useMemo, useCallback, useEffect, useRef} from 'react';
import React from 'react';
import { getDependentJobExeData } from '../../service/service';
import { dependInfoTransformData } from 'utils/common-util';
import { Box, Typography, Tooltip, GlobalStyles, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import PageWrapper from 'components/Common/PageWraper';
import Banner from 'components/Common/Banner';
import SummaryTiles from '../Widgets/SummaryTiles/SummaryTiles';
import { usePipelineRunStatus } from './hooks/usePipelineRunStatus';
import { DashboardSplitView } from './PipelineStatusChart';
import CustomizedTable from '../CustomizedTable/CustomizedTable';
import { CommonIcons, FilterIcon, ExportIcon } from 'components/CommonIcons/Icons';
import OriginalFilterDrawer from '../FilterDrawer/FilterDrawer';
import { DEFAULT_FILTERS, STATUS_FILTER_LABELS } from '../FilterDrawer/constants';
import { FilterState, GenericOption } from '../FilterDrawer/types';
import LoaderComponent from 'components/Loader';
import RunDistribution from './RunDistribution';
import RunOvertimeTrend from './RunOvertimeTrend';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { getScheduledForNextOptions,getDefaultColumns , CUSTOM_STATUS_TILE_SET, CUSTOM_STATUS_OPTIONS, CUSTOM_STATUS_TILES, CUSTOM_STATUS_TOOLTIP_TEXT, CUSTOM_STATUS_COLORS, scheduledOrder, isMiscellaneousSelected, toOptions, getTrend, columnFilterPayloadToModel, toColumnFiltersPayload } from './utils/pipelineChartUtils';
import html2canvas from 'html2canvas';
import ImagePreview from 'utils/ImagePreview';
import CaptureExportControls from 'utils/CaptureExportControls';
import { generateDashboardPdf } from 'utils/pdfExportUtil';
import type { PdfColumn } from 'utils/pdfExportUtil';
import { withShowMoreHandler } from 'utils/groupByUtil';
import { GroupedTableFormatter } from 'components/Common/GroupedTableFormatter';
import GroupByShowMore from 'components/Common/GroupByShowMore';
import { CellExpanderFormatter } from 'components/cell_expander_formatter';
import { includeGroupByMenuColumns } from './constants/chartConstants';
import ExportLimitWarningDialog from '../Modal/ExportLimitWarningDialog';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import { checkRole } from 'utils/roleUtils';
import { NON_HISTORICAL_RANGE_TYPES, TIMEZONE_MAP } from 'utils/common-util';
import moment from 'moment';
import Loader from 'components/Loader/loader';
import {filterCache }from './utils/filterCache';
import {PROGRAM_INDENT_MAP, LAYER_DEPENDENT_FIELDS, ALL_PROGRAM_FIELDS, moreFilterFields, defaultFields } from './constants/filterConstants'
import { useColumnFilters } from 'components/ColumnFilters/useColumnFilters';
import { useBanner } from 'components/Common/Banner/hooks/useBanner';

const mergeColumnFilterPayload = (
  current: any[],
  next: any[],
  changedColumnKey: string | null
) => {
  if (!changedColumnKey) return next;

  const nextById = new Map(next.map((filter) => [filter.id, filter]));
  const merged = current.filter(
    (filter) => filter.id !== changedColumnKey && !nextById.has(filter.id)
  );
  return [...merged, ...next];
};

interface Row {
  id: number;
  orderDate: any;
  jobId: string;
  jobName: string;
  dependentData: any;
  jobStatus: string;
  oDate: string;
  actualStartTime: string;
  actualEndTime: string;
  slaStatus: string;
  isLineageExpanded: boolean;
  isLineageChildren?: boolean;
  lineageChildren: Row[];
  hasDependent: boolean;
  className: string;
  eleLevel: string;
  exeId?: number;
  application: string;
  subapplication: string;
  folderNames: string;
  subFolderNames: string;
  triggerName?: string;
  adfName?: string;
  adfUrl?: string;
  silverTableName?: string | null;
  dataAssetName?: string | null;
  workspaceName?: string;
  sourceTable?: string;
  targetTable?: string;
  businessStream?: string;
  avg_duration_ms?: number;
  custom_status?:any;
  bronze_table_name?: string | null;
  date?: string;
  domain?: string | null;
  duration_ms?: number;
  endDate?: string | null;
  estimated_end_time?: string;
  execution_status?: string;
  hour?: number;
  is_recurrence?: boolean;
  job_id?: string;
  job_name?: string;
  job_type?: string;
  program_name?: string;
  recurrence_per_day?: number;
  resource_group?: string;
  resource_name?: string;
  run_url?: string;
  sector?: string;
  silver_table_name?: string | null;
  source_subcategory?: string | null;
  source_type?: string;
  startDate?: string | null;
  subscription_id?: string;
  subscription_name?: string;
  total_exe_count?: number;
  trigger_name?: string | null;
  trigger_type?: string | null;
  pipelineLineage?:boolean;
  lineageJobRunId?: number;
  productShortName?: string | null;
  expected_start_time?: string;
  schedule_type?:string;
  timezone?:any;
  schedule_missed?:boolean;
  last_run_start_time?:string;
}

  type ExpandLogicParams = {
    row: any;
    rowIndex: number;
    newRows: Row[];
    startDate: any;
    endDate: any;
    resourceTypes?: any;
    name: number;
  };
  

const isAllSectorSelected = (sectors: any[] = []) =>
  Array.isArray(sectors) &&
  sectors.some((sector: any) =>
    String(sector?.value || sector?.label || sector || '').toLowerCase() === 'all'
  );

export const isMiscellaneousFilterSelectionDisabled = (currentFilters: any = {}) => {
  if (isAllSectorSelected(currentFilters?.sector || [])) return false;

  const hasMiscellaneousSelected = isMiscellaneousSelected(currentFilters?.sector || []);
  const selectedMiscSectors = Array.isArray(currentFilters?.miscellaneousSectors)
    ? currentFilters.miscellaneousSectors
    : [];

  return hasMiscellaneousSelected && selectedMiscSectors.length === 0;
};

const getMatchingProgramKey = (programValue: string): string | null => {
  const lowerValue = programValue.toLowerCase();

  for (const [key,] of Object.entries(PROGRAM_INDENT_MAP)) {
    if (lowerValue.includes(key)) {
      return key;
    }
  }
  return null;
};

const CustomFilterDrawer = (props: any) => {
  const { currentFilters: propFilters, filterFields: propFilterFields, onFilterChange, onApply, onReset, open, ...rest } = props;
  const [showMisc, setShowMisc] = useState(false);
  const [adjustedCurrentFilters, setAdjustedCurrentFilters] = useState<FilterState>(propFilters || {});
  const [hasInitialized, setHasInitialized] = useState(false);

  const lastSyncedPropsFiltersJsonRef = useRef<string>(JSON.stringify(propFilters || {}));
  useEffect(() => {
    const currentFiltersJson = JSON.stringify(propFilters || {});
    if (currentFiltersJson !== lastSyncedPropsFiltersJsonRef.current) {
      lastSyncedPropsFiltersJsonRef.current = currentFiltersJson;
      const updatedFilters = propFilters || {};
      setAdjustedCurrentFilters(updatedFilters);
      const hasMisc = isMiscellaneousSelected(updatedFilters.sector || []);
      setShowMisc(hasMisc && !isAllSectorSelected(updatedFilters.sector || []));
    }
  }, [propFilters]);

  useEffect(() => {
    if (open && !hasInitialized) {
      setHasInitialized(true);
      const currentFiltersJson = JSON.stringify(propFilters || {});
      const updatedFilters = propFilters || {};
      setAdjustedCurrentFilters(updatedFilters);
      const hasMisc = isMiscellaneousSelected(updatedFilters.sector || []);
      setShowMisc(hasMisc && !isAllSectorSelected(updatedFilters.sector || []));
      lastSyncedPropsFiltersJsonRef.current = currentFiltersJson;
    }
    if (!open) {
      setHasInitialized(false);
    }
  }, [open, propFilters]);



  const isApplyDisabled = useMemo(() => {
    return isMiscellaneousFilterSelectionDisabled(adjustedCurrentFilters);
  }, [adjustedCurrentFilters]);

  const processedFields = useMemo(() => {
  const programFilters = adjustedCurrentFilters.program || [];

  let selectedProgram =
    programFilters?.[0]?.value?.toLowerCase() || '';

  if (programFilters.length === 2) {
    const nonAllProgram = programFilters.find(
      (item: any) =>
        String(item.value).toLowerCase() !== 'all'
    );

    if (nonAllProgram) {
      selectedProgram =
        String(nonAllProgram.value).toLowerCase();
    }
  }

  const selectedProgramValues = programFilters.map(
    (item: any) => String(item.value).toLowerCase()
  );

  const matchingProgramKeys = Object.keys(PROGRAM_INDENT_MAP).filter(
    (key) =>
      selectedProgramValues.some((programValue) =>
        programValue.includes(key.toLowerCase())
      )
  );

  const indentKeys = [
    ...new Set(
      matchingProgramKeys.flatMap(
        (key) => PROGRAM_INDENT_MAP[key] || []
      )
    ),
  ];

  const layerValue =
    adjustedCurrentFilters.layer?.[0]?.value?.toLowerCase() || '';

  const layerField = propFilterFields?.find(
    (f: any) => f.field === 'layer'
  );

  const layerOptions = layerField?.options || [];

  const isAllSelected =
    adjustedCurrentFilters.layer?.some(
      (item: any) =>
        String(item.value).toLowerCase() === 'all'
    ) || false;

  let effectiveLayerValue = layerValue;

  if (isAllSelected) {
    const nonAllOptions = layerOptions.filter(
      (opt: any) =>
        String(opt.value).toLowerCase() !== 'all'
    );

    if (nonAllOptions.length === 1) {
      effectiveLayerValue =
        String(nonAllOptions[0].value).toLowerCase();
    }
  }

  const visibleFields = new Set<string>();

  if (selectedProgram !== '') {
    visibleFields.add('layer');
  }

  indentKeys.forEach((key) =>
    visibleFields.add(key)
  );


  const isMosaicProgram = matchingProgramKeys.includes('mosaic');

  const goldDependentFieldsForProgram = isMosaicProgram
    ? LAYER_DEPENDENT_FIELDS.gold
    : LAYER_DEPENDENT_FIELDS.gold.filter(
        (field) => field === 'goldTableName'
      );

  if (effectiveLayerValue === 'gold') {
    goldDependentFieldsForProgram.forEach(
      (field) => visibleFields.add(field)
    );
  } else if (effectiveLayerValue === 'silver') {
    LAYER_DEPENDENT_FIELDS.silver.forEach(
      (field) => visibleFields.add(field)
    );
  } else if (isAllSelected && layerOptions.length > 0) {

    LAYER_DEPENDENT_FIELDS.silver.forEach(
      (field) => visibleFields.add(field)
    );

    goldDependentFieldsForProgram.forEach(
      (field) => visibleFields.add(field)
    );
  }

  return propFilterFields.reduce(
    (acc: any[], field: any) => {

      if (defaultFields.includes(field.field)) {
        if (field.field === 'miscellaneousSectors') {
          if (!showMisc) return acc;

          acc.push({
            ...field,
            disabled: isAllSectorSelected(adjustedCurrentFilters.sector || []),
            placeholder: isApplyDisabled
              ? 'None'
              : field.placeholder,
            error: isApplyDisabled,
            errorText: isApplyDisabled
              ? 'Please select at least one value to continue'
              : undefined,
          });

          return acc;
        }

        acc.push(field);
        return acc;
      }

      if (moreFilterFields.includes(field.field)) {
        acc.push(field);
        return acc;
      }

      if (!visibleFields.has(field.field)) {
        return acc;
      }

      if (indentKeys.includes(field.field)) {
        acc.push({
          ...field,
          isIndented: true,
          indentLevel: 1,
        });

        return acc;
      }

      if (field.field === 'layer') {
        acc.push({
          ...field,
          isIndented: true,
          indentLevel: 1,
        });

        return acc;
      }

      if (
        LAYER_DEPENDENT_FIELDS.gold.includes(field.field) ||
        LAYER_DEPENDENT_FIELDS.silver.includes(field.field)
      ) {
        let isVisible = false;

        if (
          effectiveLayerValue === 'gold' &&
          LAYER_DEPENDENT_FIELDS.gold.includes(field.field) &&
          goldDependentFieldsForProgram.includes(field.field)
        ) {
          isVisible = true;
        } else if (
          effectiveLayerValue === 'silver' &&
          LAYER_DEPENDENT_FIELDS.silver.includes(field.field)
        ) {
          isVisible = true;
        } else if (
          isAllSelected &&
          layerOptions.length > 1 &&
          (LAYER_DEPENDENT_FIELDS.silver.includes(field.field) ||
            goldDependentFieldsForProgram.includes(field.field))
        ) {
          isVisible = true;
        }

        if (isVisible) {
          acc.push({
            ...field,
            isIndented: true,
            indentLevel: 1,
          });
        }

        return acc;
      }

      acc.push(field);
      return acc;
    },
    []
  );
}, [
  propFilterFields,
  adjustedCurrentFilters,
  showMisc,
  isApplyDisabled,
]);


  useEffect(() => {
    const selectedProgram = adjustedCurrentFilters.program?.[0]?.value?.toLowerCase() || '';
    const layerValue = adjustedCurrentFilters.layer?.[0]?.value?.toLowerCase() || '';

    const matchingKey = getMatchingProgramKey(selectedProgram);

    if (matchingKey === 'edf' && !layerValue) {
      const layerField = propFilterFields?.find((f: any) => f.field === 'layer');
      if (layerField) {
        const silverOption = layerField.options.find((opt: any) =>
          String(opt.value).toLowerCase() === 'silver'
        );
        if (silverOption) {
          setAdjustedCurrentFilters((prev: any) => ({
            ...prev,
            layer: [silverOption]
          }));
        }
      }
    }
  }, [adjustedCurrentFilters.program]);

  const handleResetDrawer = useCallback(() => {
    setAdjustedCurrentFilters({ ...DEFAULT_FILTERS });
    setShowMisc(false);

  }, []);

  const handleLocalFilterChange = useCallback((field: string, newValue: any) => {
    setAdjustedCurrentFilters((prev: any) => {
      const nextFilters = { ...prev, [field]: newValue };
      if (field === 'sector') {
        const hasMisc = isMiscellaneousSelected(newValue);
        const hasAllSectors = isAllSectorSelected(newValue);
        setShowMisc(hasMisc && !hasAllSectors);
        if (hasAllSectors) {
          nextFilters.miscellaneousSectors = [];
        } else if (hasMisc) {
          const miscField = propFilterFields?.find((f: any) => f.field === 'miscellaneousSectors');
          const miscOptions = miscField?.options || [];
          if (miscOptions.length > 0 && (!nextFilters.miscellaneousSectors || nextFilters.miscellaneousSectors.length === 0)) {
            nextFilters.miscellaneousSectors = [{ label: 'All', value: 'all' }];
          }
        } else {
          nextFilters.miscellaneousSectors = [];
        }
      }

      if (field === 'program') {
        ALL_PROGRAM_FIELDS.forEach(key => {
          delete nextFilters[key];
          filterCache.clear(key);
        });

        const selectedProgram = newValue?.[0]?.value?.toLowerCase() || '';
        const matchingKey = getMatchingProgramKey(selectedProgram);

        if (matchingKey === 'edf') {
          const layerField = propFilterFields?.find((f: any) => f.field === 'layer');
          if (layerField) {
            const silverOption = layerField.options.find((opt: any) =>
              String(opt.value).toLowerCase() === 'silver'
            );
            if (silverOption) {
              nextFilters.layer = [silverOption];
            }
          }
        }
      }

      if (field === 'layer') {

        Object.values(LAYER_DEPENDENT_FIELDS).flat().forEach(key => {
          delete nextFilters[key];
          filterCache.clear(key);
        });
      }

      if (field === 'sourceType') {
        filterCache.clear('sourceSubcategory');
        filterCache.clear('orchestratorScheduler');
      }

      filterCache.clear(field);

      return nextFilters;
    });

    if (typeof onFilterChange === 'function') {
      onFilterChange(field, newValue);
    }
  }, [propFilterFields, onFilterChange]);

  return (
    <OriginalFilterDrawer
      {...props}
      filterFields={processedFields}
      currentFilters={adjustedCurrentFilters}
      onReset={handleResetDrawer}
      onFilterChange={handleLocalFilterChange}
      applyDisabled={isApplyDisabled}
    />
  );
};

const FilterDrawer = CustomFilterDrawer;

const CHART_CARDS = [
  { key: 0, label: 'Run Distribution',    sub: 'Latest', Icon: CommonIcons.TrendBySectorIcon },
  { key: 1, label: 'Run Trends',   sub: 'All Status',  Icon: CommonIcons.TrendByProgramIcon },
  { key: 2, label: 'Job Type By Status',  sub: 'View All',  Icon: CommonIcons.JobTypeStatusIcon },
  { key: 3, label: 'Schedule By Hour',    sub: 'View All',     Icon: CommonIcons.MostImpactedAppIcon },
];

const CustomStatusChip = ({ row }: any) => {
  const raw = row?.custom_status;

  if (!raw) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.5,
          py: 0.5,
          backgroundColor: '#9E9E9E',
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.7rem',
          height: '24px',
          borderRadius: '4px',
          minWidth: '70px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        --
      </Box>
    );
  }

  const normalized = String(raw).trim().toUpperCase();
  const style = CUSTOM_STATUS_COLORS[normalized] ?? '#9E9E9E';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '70px',
        width: '100%',
        textAlign: 'center',
      }}
    >
      {normalized}
    </Box>
  );
};

const StatusChip = ({ row }: any) => {
  const rawStatus = row.execution_status || row.jobStatus;

  if (!rawStatus) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1.5,
          py: 0.5,
          backgroundColor: '#9E9E9E',
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.7rem',
          height: '24px',
          borderRadius: '4px',
          minWidth: '70px',
          width: '100%',
          textAlign: 'center',
        }}
      >
        --
      </Box>
    );
  }

  const normalizeStatus = (status: string) => {
    const s = status?.toLowerCase();

    if (['abort', 'aborted'].includes(s)) return 'Aborted';
    if (['success', 'successful', 'succeeded'].includes(s)) return 'Successful';
    if (['fail', 'failed'].includes(s)) return 'Failed';
    if (['inprogress', 'in progress'].includes(s)) return 'In Progress';
    if (['delayed'].includes(s)) return 'Delayed';
    if (['scheduled'].includes(s)) return 'Scheduled';

    return status;
  };

  const normalized = normalizeStatus(rawStatus);
  const colorMap: Record<string, string> = {
    Aborted: '#F57600',
    Successful:'#01C008',
    Failed: '#FE2626',
    'In Progress':'#ECB914',
    Delayed: '#FF38B0',
    Scheduled: '#9E9E9E',
  };
  const style = row.bgColor ?? colorMap[normalized] ?? '#9E9E9E';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.5,
        py: 0.5,
        backgroundColor: style,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: '24px',
        borderRadius: '4px',
        minWidth: '70px',
        width: '100%',
        textAlign: 'center',
      }}
    >
      {normalized.toUpperCase()}
    </Box>
  );
};

const renderStatusFormatter = ({ row }: any) => <StatusChip row={row} />;

const renderDateFormatter = ({ row }: any) => {
  if (!row.date && !row.orderDate) return '--';

  const [yyyy, mm, dd] = row.date?.split('-') || row.orderDate?.split('-');
  return <span>{`${mm}-${dd}-${yyyy}`}</span>;
};

const renderTimeFormatter = (
  field: string,
  ianaTimezone: string,
) => ({ row }: any) => {
  if (!row[field]) return '--';

  const date = new Date(row[field]);

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: ianaTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;

  const time = date.toLocaleTimeString('en-US', {
    timeZone: ianaTimezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  return <span>{`${month}-${day}-${year} ${time}`}</span>;
};

const renderNumberFormatter = (field: string) => ({ row }: any) => (
  <span>{row[field] != null && !isNaN(row[field]) ? Number(row[field]) : '--'}</span>
);

const renderCommaFormatter = (field: string) => ({ row }: any) => (
  <span>
    {row[field] != null && !isNaN(row[field])
      ? Number(row[field]).toLocaleString()
      : '--'}
  </span>
);

export const renderTimeFormattter = (field: string) => ({ row }: any) => {
  const rawValue = row?.[field];

  if (rawValue == null || Number.isNaN(Number(rawValue))) {
    return <span>{'--'}</span>;
  }

  const minutes = Number(rawValue) / 60000;
  const formatted = Number(minutes.toFixed(2));

  return <span>{formatted}</span>;
};

const formatDurationFilterOption = (value: any): string => {
  const milliseconds = Number(value);
  if (value == null || value === '' || Number.isNaN(milliseconds)) {
    return value == null || value === '' ? '(Blank)' : String(value);
  }
  return String(Number((milliseconds / 60000).toFixed(2)));
};

const renderRecurringFormatter = (field: string) => ({ row }: any) => {
  let value = '';
  if (row[field] === true) {
    value = 'Yes';
  } else if (row[field] === false) {
    value = 'No';
  } else {
    value = '--';
  }
  return <span>{value}</span>;
};

const sortScheduleCategories = (values: string[]): string[] => {
  const priority: Record<string, number> = {
    All: -1,
    Adhoc: 0,
    'Event Based': 1,
    Daily: 2,
    Weekly: 3,
    Monthly: 4,
    Quarterly: 5,
    'Half Yearly': 6,
    Yearly: 7,
  };

  return [...values].sort((a, b) => {
    const indexA = priority[a] ?? Number.MAX_SAFE_INTEGER;
    const indexB = priority[b] ?? Number.MAX_SAFE_INTEGER;

    if (indexA !== indexB) {
      return indexA - indexB;
    }

    return a.localeCompare(b);
  });
};

// Helper to get label from TILES_CONFIG
const getTileLabel = (key: string) => {
  const tile = TILES_CONFIG.find(t => t.key === key);
  if (!tile) return '';
  if (['All', 'UniqueJobs'].includes(key)) return tile.label;
  if (CUSTOM_STATUS_OPTIONS.includes(key)) {
    return `${tile.label}`;
  }
  return `${tile.label} Jobs`;
};

const TILES_CONFIG = [
  { key: 'All', label: 'Total Job Runs', summaryKey: 'totalJobs' },
  { key: 'UniqueJobs', label: 'Total Unique Jobs', summaryKey: 'uniqueJobs' },
  { key: 'Successful', label: 'Successful', summaryKey: 'successful' },
  { key: 'Aborted', label: 'Aborted', summaryKey: 'aborted' },
  { key: 'InProgress', label: 'In Progress', summaryKey: 'inProgress' },
  { key: 'Failed', label: 'Failed', summaryKey: 'failed' },
  { key: 'Scheduled', label: 'Scheduled', summaryKey: 'scheduled' },
  ...CUSTOM_STATUS_TILES,
  { key: 'SHORT RUN', label: 'Short Run',  summaryKey: 'shortRunCount' },
];

const transformScheduledSummaryData = (rawSummary: any) => {
  if (!rawSummary?.data) return null;

  const scheduledCount = Number(rawSummary.data.Scheduled || 0);

  return {
    totalJobs: {
      total: scheduledCount,
      difference: 0,
      trend: getTrend(0),
    },
    uniqueJobs: { total: 0, difference: 0, trend: getTrend(0) },
    successful: { total: 0, difference: 0, trend: getTrend(0) },
    aborted: { total: 0, difference: 0, trend: getTrend(0) },
    inProgress: { total: 0, difference: 0, trend: getTrend(0) },
    delayed: { total: 0, difference: 0, trend: getTrend(0) },
    failed: { total: 0, difference: 0, trend: getTrend(0) },
    shortRunCount: { total: 0, difference: 0, trend: getTrend(0) },
    hung: { total: 0, difference: 0, trend: getTrend(0) },
    longRunning: { total: 0, difference: 0, trend: getTrend(0) },
    scheduled: { total: scheduledCount, difference: 0, trend: getTrend(0) },
  };
};

const transformCustomStatusSummaryData = (rawSummary: any) => {
  if (!rawSummary?.data) return null;
  return {
    totalJobs: { total: 0, difference: 0, trend: getTrend(0) },
    uniqueJobs: { total: 0, difference: 0, trend: getTrend(0) },
    successful: { total: 0, difference: 0, trend: getTrend(0) },
    aborted: { total: 0, difference: 0, trend: getTrend(0) },
    inProgress: { total: 0, difference: 0, trend: getTrend(0) },
    delayed: { total: 0, difference: 0, trend: getTrend(0) },
    failed: { total: 0, difference: 0, trend: getTrend(0) },
    shortRunCount: { total: 0, difference: 0, trend: getTrend(0) },
    hung: { total: rawSummary.data['HUNG'], difference: 0, trend: 'NO_CHANGE' },
    longRunning: { total: rawSummary.data['LONG RUNNING'], difference: 0, trend: 'NO_CHANGE' },
    scheduled: { total: 0, difference: 0, trend: getTrend(0) },
  };
};

const transformCommonSummaryData = (rawSummary: any) => {
  if (!rawSummary) return null;

  const data = rawSummary.data || {};
  const { result = {}, resultLast1hr = {}, uniqueJobCount = 0,
    uniqueJobCountLast1hr = 0, shortRunCount = 0 } = data;

  const getKpi = (key: string) => {
    const total = result?.[key] || 0;
    const difference = resultLast1hr?.[key] || 0;
    const trend = getTrend(difference);
    return {
      total,
      difference,
      trend
    };
  };

  const total = Object.values(result).reduce(
    (sum: number, val: any) => sum + val,
    0
  );

  const difference = Object.values(resultLast1hr).reduce(
    (sum: number, val: any) => sum + val,
    0
  );

  return {
    totalJobs: {
      total,
      difference,
      trend: getTrend(difference),
    },
    uniqueJobs: {
      total: uniqueJobCount,
      difference: uniqueJobCountLast1hr,
      trend: getTrend(uniqueJobCountLast1hr),
    },
    successful: getKpi("Successful"),
    aborted: getKpi("Aborted"),
    inProgress: getKpi("In Progress"),
    delayed: getKpi("Delayed"),
    failed: getKpi("Failed"),
    scheduled: getKpi("Scheduled"),
    shortRunCount: {
      total: shortRunCount,
      difference: 0,
      trend: 'NO_CHANGE'
    },
    hung: getKpi("Hung"),
    longRunning: getKpi("Long Running")
  };
};

const transformSummaryData = (rawSummary: any) => {
  if (!rawSummary) return null;

  const data = rawSummary.data || {};

  const isScheduledFormat = data.Scheduled != null && !data.result && !data.resultLast1hr;
  if (isScheduledFormat) return transformScheduledSummaryData(rawSummary);

  const isCustomStatusFormat = data.HUNG != null && !data.Scheduled && !data.result && !data.resultLast1hr;
  // Read data from custom status kpi api and merge with other kpis/widgets count
  if (isCustomStatusFormat) return transformCustomStatusSummaryData(rawSummary);

  const base = transformCommonSummaryData(rawSummary);

  return {
    ...base
  };
};

const ChartContent = ({
  handleSourceSliceClick,
  activeBottomTab,
  runDistribution,
  runOvertimeTrend,
  runOvertimeCards,
  jobTypeByStatusData,
  scheduleByHourData,
  isRunDistributionLoading,
  isRunTrendLoading,
  isRunTrendCardsLoading,
  isJobTypeStatusLoading,
  isScheduleByHourLoading
}: any) => {

  const chartContent = [
    <RunDistribution key="runDistribution" data={runDistribution} isLoading={isRunDistributionLoading} onSliceClick={handleSourceSliceClick}/>,
    <RunOvertimeTrend key="runOvertimeTrend" xAxisData={runOvertimeTrend?.xAxisData || []} series={runOvertimeTrend?.series || []} cards={runOvertimeCards || []} isLoading={isRunTrendLoading} isCardsLoading={isRunTrendCardsLoading} />,
    <DashboardSplitView key="jobTypeByStatus" isLoading={isJobTypeStatusLoading} title="Job Type By Status" chartCategories={jobTypeByStatusData?.categories || []} chartSeries={jobTypeByStatusData?.series || []} tableColumns={jobTypeByStatusData?.tableColumns || []} tableData={jobTypeByStatusData?.tableData || []}  />,
    <DashboardSplitView key="scheduleByHour" isLoading={isScheduleByHourLoading} isScheduleByHour={true} title="Schedule By Hour" chartCategories={scheduleByHourData?.categories || []} chartSeries={scheduleByHourData?.series || []} tableColumns={scheduleByHourData?.tableColumns || []} tableData={scheduleByHourData?.tableData || []}  />,
  ]
  return chartContent[activeBottomTab];
};

const getConditionalTimeFormatter =
  (
    actualField: string,
    fallbackField: string,
    ianaTimezone: string
  ) =>
  ({ row }: any) => {
    const field = row?.[actualField] ? actualField : fallbackField;
    return renderTimeFormatter(field, ianaTimezone)({ row });
  };

const columns = (
    ianaTimezone: string,
    activeTile: string,
    toggleJobDependency: (row: number, name: any, eleLevel: string, rowExpand: any) => void,
    columnDetails: Record<string, string> = {}
  ) => {
  const isScheduled = activeTile === 'Scheduled';
  const withColumnDetails = (column: any) => ({
    ...column,
    columnDetails,
  });

  const baseColumns = [
    {
      key: 'date',
      name: isScheduled ? 'SCHEDULED DATE' : 'DATE',
      width: 220,
      formatter: withShowMoreHandler(renderDateFormatter),
      resizable: true,
      sortable: true
    },
    {
      key: 'hour',
      name: isScheduled ? 'SCHEDULED HOUR' : 'HOUR',
      width: 220,
      formatter: withShowMoreHandler(isScheduled?({ row }: any) => row.hour || '--':renderNumberFormatter('hour')),
      resizable: true,
      sortable: true
    },
    {
      key: 'expected_start_time',
      name: 'EXPECTED START TIME',
      width: 230,
      formatter: withShowMoreHandler(({ row }: any) => row.expected_start_time || '--'),
      resizable: true,
      sortable: true 
    },
    ...(isScheduled ? [{
      key: 'last_run_start_time',
      name: 'TODAY\'S RUN START',
      width: 230,
      formatter: withShowMoreHandler(({ row }: any) => row.last_run_start_time || '--'),
      resizable: true,
      sortable: true
    }] : []),
    ...(isScheduled ? [{
      key: 'schedule_missed',
      name: 'SCHEDULE MISSED',
      width: 230,
      formatter: withShowMoreHandler(({ row }: any) =>
        row.schedule_missed === true ? 'True' : row.schedule_missed === false ? 'False' : '--'
      ),
      resizable: true,
      sortable: true
    },
    {
      key: 'origin',
      name: 'ORIGIN',
      width: 230,
      formatter: withShowMoreHandler(({ row }: any) => row.origin || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'destination',
      name: 'DESTINATION',
      width: 230,
      formatter: withShowMoreHandler(({ row }: any) => row.destination || '--'),
      resizable: true,
      sortable: true
    }
  ] : []),
    {
      key: 'job_type',
      name: 'JOB TYPE',
      width: 130,
      formatter: withShowMoreHandler(({ row }: any) => row.job_type || row.jobType || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'job_id',
      name: 'JOB ID',
      minWidth: 250,
      maxWidth: 750,
      formatter: withShowMoreHandler(({ row }: any) => row.job_id || row.jobId || '--'),
      resizable: true,
      sortable: true
    },
    ...(!isScheduled ? [{
      key: 'run_id',
      name: 'JOB RUN URL',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => {
        const isAzureSource = row.source_type === 'ADF' || row.source_type === 'ADB';
        if (!isAzureSource) return '-';
        const text = row.run_url || row.adfUrl || '--';
        if (row.run_url || row.adfUrl) {
          return (
            <a
              href={row.run_url || row.adfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
            >
              {text}
            </a>
          );
        }
        return <span>{text}</span>;
      }),
      resizable: true,
      sortable: true
    }] : []),
    {
      key: 'job_name',
      name: 'JOB NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row, isCellSelected }: any) => {
      const hasChildren = hasRowChildren(row);

      function hasRowChildren(row: any) {
        const hasChild =
          row.hasDependent ||
          row.hasDependant ||
          row.lineageChildren?.length

        const isFailedStatus = ['failed', 'delayed', 'aborted'].includes(
          row?.execution_status?.toLowerCase()
        );

        return Boolean(hasChild && isFailedStatus);
      }

    const className = [
      row.className,
      row.eleLevel,
      row.jobStatus,
      row.isLineageChildren ? 'child-view' : 'parent-view',
      hasChildren ? 'has-children' : 'single',
    ].join(' ');

        row={...row, isLineageExpanded: row.isLineageExpanded ?? false}; // Ensure isLineageExpanded is defined
        return (
          <Box className={className} display="flex" alignItems="center" gap={0.5}>
            {hasChildren && (
              <CellExpanderFormatter
                isCellSelected={isCellSelected}
                expanded={Boolean(row.isLineageExpanded) &&  ['failed', 'delayed', 'aborted'].includes(
              row?.execution_status?.toLowerCase()
            )}
                onCellExpand={() => toggleJobDependency(row.id, row.dependentData, row.eleLevel, row.isLineageExpanded)}
              />
            )}

            <Typography
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {row.job_name || row.jobName || "--"}
            </Typography>
          </Box>
        );
      }),
      sortable: true,
      resizable: true
    },
    {
      key: 'execution_status',
      name: 'STATUS',
      width: 150,
      renderCell: StatusChip,
      formatter: withShowMoreHandler(renderStatusFormatter),
      cellClass: 'status-center',
      resizable: true,
      sortable: true,
      isFilterOptionDisabled: isStatusFilterOptionDisabled,
    },
    ...(!isScheduled ? [{
      key: 'start_time',
      name: 'START TIME',
      width: 160,
      formatter: withShowMoreHandler(getConditionalTimeFormatter('actualStartTime', 'start_time', ianaTimezone)),
      resizable: true,
      sortable: true,
    }] : []),
    ...(
      !isScheduled ? [
        {
          key: 'end_time',
          name: 'END TIME',
          width: 160,
          formatter: withShowMoreHandler(getConditionalTimeFormatter('actualEndTime', 'end_time', ianaTimezone)),
          resizable: true,
          sortable: true,
        },
      ] : []
    ),
    {
      key: 'source_type',
      name: 'SOURCE',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'schedule_info',
      name: 'SCHEDULE INFO',
      width: 300,
      formatter: withShowMoreHandler(({ row }: any) => row.schedule_info || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'timezone',
      name: 'TIMEZONE',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.timezone || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'program_name',
      name: 'PROGRAM',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.program_name || row.programName || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'sector',
      name: 'SECTOR',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.sector || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'success_count',
      name: 'Success Count',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.success_count || '--'),
      resizable: true,
      sortable: false
    },
    {
      key: 'failed_count',
      name: 'Failed Count',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.failed_count || '--'),
      resizable: true,
      sortable: false
    },
    {
      key: 'gold_count',
      name: 'Gold Count',
      width: 150,
      formatter: withShowMoreHandler(({ row }: any) => row.gold_count || '--'),
      resizable: true,
      sortable: false
    },
    ...(
      !isScheduled
        ? [
          {
            key: 'duration_ms',
            name: 'RUN TIME MIN',
            width: 160,
            formatter: withShowMoreHandler(renderTimeFormattter('duration_ms')),
            formatFilterValue: formatDurationFilterOption,
            resizable: true,
            sortable: true
          },
          {
            key: 'estimated_end_time',
            name: 'EXPECTED END TIME',
            width: 200,
            formatter: withShowMoreHandler(renderTimeFormatter('estimated_end_time', ianaTimezone)),
            resizable: true,
            sortable: true
          },
          {
            key: 'avg_duration_ms',
            name: 'AVG RUN TIME MIN',
            width: 200,
            formatter: withShowMoreHandler(renderTimeFormattter('avg_duration_ms')),
            formatFilterValue: formatDurationFilterOption,
            resizable: true,
            sortable: true
          },
          {
            key: 'total_exe_count',
            name: 'TOTAL RECORDS',
            width: 180,
            formatter: withShowMoreHandler(renderCommaFormatter('total_exe_count')),
            resizable: true,
            sortable: true
          },
        ]
        : []
    ),
    {
      key: 'is_recurrence',
      name: 'RECURRING',
      width: 150,
      formatter: withShowMoreHandler(renderRecurringFormatter('is_recurrence')),
      resizable: true,
      sortable: true
    },
    {
      key: 'recurrence_per_day',
      name: 'RECURRENCES PER DAY',
      width: 230,
      formatter: withShowMoreHandler(renderCommaFormatter('recurrence_per_day')),
      resizable: true,
      sortable: true
    },
    {
      key: 'trigger_name',
      name: 'TRIGGER NAME',
      width: 170,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type === 'ADF' ? (row.trigger_name || row?.triggerName || '--') : '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'trigger_type',
      name: 'TRIGGER TYPE',
      width: 170,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type === 'ADF' ? (row.trigger_type || '--') : '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'data_asset_name',
      name: 'DATA ASSET NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type === 'ADF' ? (row.data_asset_name || '--') : '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'market',
      name: 'MARKET',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) =>
        Array.isArray(row.market) ? row.market.join(', ') || '--' : row.market || '--'
      ),
      resizable: true,
      sortable: true
    },
    {
      key: 'gold_table_name',
      name: 'GOLD TABLE NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type === 'ADF' ? (row.gold_table_name || '--') : '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'silver_table_name',
      name: 'SILVER TABLE NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type === 'ADF' ? (row.silver_table_name || '--') : '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'bronze_table_name',
      name: 'BRONZE TABLE NAME',
      width: 210,
      formatter: withShowMoreHandler(({ row }: any) => row.source_type === 'ADF' ? (row.bronze_table_name || '--') : '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'subscription_name',
      name: 'SUBSCRIPTION NAME',
      width: 210,
      formatter: withShowMoreHandler(({ row }: any) => row.subscription_name || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'subscription_id',
      name: 'SUBSCRIPTION ID',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.subscription_id || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'resource_group',
      name: 'RESOURCE GROUP',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.resource_group || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'resource_name',
      name: 'RESOURCE NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.resource_name || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'job_no',
      name: 'JOB NUMBER',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.job_no || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'application_name',
      name: 'APPLICATION NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.application_name || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'folder_name',
      name: 'FOLDER NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.folder_name || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'domain',
      name: 'DOMAIN',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.domain || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'productShortName',
      name: 'PRODUCT SHORT NAME',
      width: 250,
      formatter: withShowMoreHandler(({ row }: any) => row.productShortName || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'program_job_name',
      name: 'PROGRAM JOB NAME',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.program_job_name || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'gdo',
      name: 'GDO',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.gdo || '--'),
      resizable: true,
      sortable: true
    },
    {
      key: 'layer',
      name: 'LAYER',
      width: 200,
      formatter: withShowMoreHandler(({ row }: any) => row.layer || '--'),
      resizable: true,
      sortable: true
    },
  ];
const ordered = activeTile === 'Scheduled'
  ? [
      ...scheduledOrder
        .map((key) => baseColumns.find((c: any) => c.key === key))
        .filter(Boolean),
      ...baseColumns.filter(
        (c: any) =>
          !scheduledOrder.includes(c.key) &&
          !['custom_status', 'start_time', 'end_time', 'duration_ms',
            'estimated_end_time', 'avg_duration_ms', 'total_exe_count',
            'run_id', 'trigger_name', 'trigger_type', 'data_asset_name',
            'gold_table_name', 'silver_table_name', 'bronze_table_name',
            'subscription_name', 'subscription_id', 'resource_group',
            'resource_name', 'job_no', 'application_name', 'folder_name',
            'productShortName', 'program_job_name', 'is_recurrence',
            'recurrence_per_day', 'job_type', 'job_id', 'execution_status',
            'source_type'].includes(c.key)
      ),
    ]
  : baseColumns;
  return ordered.map(withColumnDetails);
};

const normalizeStatusValue = (v: any): string =>
  String(v?.value ?? v ?? '').toLowerCase().replace(/[\s_-]/g, '');

const isInProgress = (v: any) => normalizeStatusValue(v) === 'inprogress';

export const isStatusFilterOptionDisabled = (option: any, selected: any[] = []): boolean => {
  if (!selected.length) return false;

  const inProgressSelected = selected.some(isInProgress);
  const otherSelected = selected.some((s) => !isInProgress(s));

  if (inProgressSelected) return !isInProgress(option);  // disable everything except In Progress
  if (otherSelected) return isInProgress(option);        // disable only In Progress
  return false;
};

const TableWithColumnFilters = ({
  key,
  p,
  activeContent,
  activeTile,
  isCustomStatusTile,
  totalElements,
  isTotalCountLoading,
  page,
  pageSize,
  sortingInfo,
  setPage,
  setPageSize,
  setSortingInfo,
  handleSourceSliceClick,
  activeBottomTab,
  runDistribution,
  runOvertimeTrend,
  runOvertimeCards,
  jobTypeByStatusData,
  scheduleByHourData,
  isRunDistributionLoading,
  isRunTrendLoading,
  isRunTrendCardsLoading,
  isJobTypeStatusLoading,
  isScheduleByHourLoading,
  tableTitle,
  setGroupBy,
  displayData,
  groupByValue,
  tableColumns,
  includeGroupByMenuColumns,
  onMetadataUpdatesClick,
  dependentInfoLoading,
  setDependentInfoLoading,
  loadJobsList,
  getColumnFilterValues,
  isPrevNextPagination,
  onRequestReset,
}: any) => {
  const getDefaultColumnsFromColumns = useCallback(
    (columns: any[]) => columns.map((col) => col.name),
    []
  );

  const defaultColumns = useMemo(() => {
    if (groupByValue && groupByValue !== 'none') {
      return getDefaultColumnsFromColumns(tableColumns);
    }
    return getDefaultColumns(activeTile, groupByValue);
  }, [tableColumns, groupByValue, activeTile, getDefaultColumnsFromColumns]);

  const savedColumnFilterState = filterCache.getColumnFilterState<any>();
  const columnFiltersPayloadRef = useRef<any[]>(savedColumnFilterState?.payload ?? []);

  const [forceInProgress, setForceInProgress] = useState(isCustomStatusTile);

  const initialFilterModel = useMemo(() => {
    const base =
      savedColumnFilterState?.filterModel ??
      columnFilterPayloadToModel(savedColumnFilterState?.payload);

    if (!isCustomStatusTile) {
      // strip implicit InProgress seed
      if (
        base?.execution_status &&
        Array.isArray(base.execution_status.selOptions) &&
        base.execution_status.selOptions.length > 0 &&
        base.execution_status.selOptions.every((s: any) =>
          ['inprogress', 'in progress'].includes(String(s).toLowerCase())
        )
      ) {
        const { execution_status, ...rest } = base;
        return rest;
      }
      return base;
    }

    const existingSel = Array.isArray(base?.execution_status?.selOptions)
      ? base!.execution_status.selOptions
      : [];
    const hasUserChoice =
      existingSel.length > 0 &&
      existingSel.some(
        (s: any) =>
          !['all', 'inprogress', 'in progress'].includes(String(s).toLowerCase())
      );
    if (hasUserChoice) return base;

    return {
      ...(base || {}),
      execution_status: {
        values: ['In Progress'],
        selOptions: ['In Progress'],
        condition: undefined,
      },
    };
  }, [savedColumnFilterState, isCustomStatusTile]);

  const columnFilters = useColumnFilters({
    columns: tableColumns,
    rows: displayData,
    mode: 'server',
    initialFilterModel,
    onChange: ({ filterModel, sortModel, changedColumnKey }) => {
      setPage(1);

      let effectiveFilterModel = filterModel;
      let needsRemount = false;

      if (isCustomStatusTile) {
        const currentStatus = effectiveFilterModel?.execution_status;
        const selValues = Array.isArray(currentStatus?.selOptions)
          ? currentStatus.selOptions
          : [];

        const hasNonInProgress = selValues.some(
          (s: any) =>
            !['all', 'inprogress', 'in progress'].includes(String(s).toLowerCase())
        );

        if (!currentStatus || (!hasNonInProgress && selValues.length === 0)) {
          effectiveFilterModel = {
            ...effectiveFilterModel,
            execution_status: {
              values: ['In Progress'],
              selOptions: ['In Progress'],
              condition: undefined,
            },
          };
          setForceInProgress(true);
          needsRemount = true;
        } else if (hasNonInProgress) {
          setForceInProgress(false);
        }
      }

      const nextColumnFilters = toColumnFiltersPayload(effectiveFilterModel);
      const currentColumnFilters = columnFiltersPayloadRef.current;
      const columnFiltersPayload = mergeColumnFilterPayload(
        Array.isArray(currentColumnFilters) ? currentColumnFilters : [],
        nextColumnFilters,
        changedColumnKey
      );
      columnFiltersPayloadRef.current = columnFiltersPayload;

      // Decide what to cache:
      //  - On custom tiles: cache the seeded model so remounts re-seed.
      //  - On non-custom tiles: strip any implicit-only InProgress so it doesn't leak.
      let modelToCache = effectiveFilterModel;
      if (isCustomStatusTile) {                          // ← was `!isCustomStatusTile`
        const es = modelToCache?.execution_status;
        const esVals = Array.isArray(es?.selOptions) ? es.selOptions : [];
        const onlyInProgress =
          esVals.length > 0 &&
          esVals.every((s: any) =>
            ['inprogress', 'in progress'].includes(String(s).toLowerCase())
          );
        if (onlyInProgress) {
          const { execution_status, ...rest } = modelToCache;
          modelToCache = rest;
        }
      }

      if (Object.keys(modelToCache).length === 0 && sortModel.length === 0) {
        filterCache.clearColumnFilterState();
      } else {
        filterCache.saveColumnFilterState({
          filterModel: modelToCache,
          sortModel,
          payload: toColumnFiltersPayload(modelToCache),
        });
      }

      const nextSortingInfo = sortModel.length
        ? { sortBy: sortModel[0].columnKey, sortDir: sortModel[0].direction }
        : { sortBy: '', sortDir: '' };
      setSortingInfo(nextSortingInfo);
      loadJobsList(columnFiltersPayload, nextSortingInfo);

      if (needsRemount && typeof onRequestReset === 'function') {
        setTimeout(() => onRequestReset(), 0);
      }
    },

  });

  return (
    <Box
      id="ua-tab-content"
      sx={{
        backgroundColor: p.white.main,
        p: 3,
        pt: activeContent === 'chart' ? 5 : 3,
        position: 'relative',
        minHeight: '300px',
      }}
    >
      {dependentInfoLoading && <Loader />}
      {activeContent === 'table' ? (
        <CustomizedTable
          tableTitle={tableTitle}
          tableData={displayData}
          total={totalElements}
          isTotalCountLoading={isTotalCountLoading}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onSorting={setSortingInfo}
          sortingInfo={sortingInfo}
          defaultColumns={defaultColumns}
          tableType={`pipeline-run-status${activeTile}`}
          columns={tableColumns}
          onGroupByChange={setGroupBy}
          groupBy={groupByValue}
          includeGroupByMenuColumns={includeGroupByMenuColumns}
          onMetadataUpdatesClick={onMetadataUpdatesClick}
          enableColumnFilter
          columnFilterController={columnFilters}
          getColumnFilterValues={getColumnFilterValues}
          isPrevNextPagination={isPrevNextPagination}
        />
      ) : (
        <ChartContent
          handleSourceSliceClick={handleSourceSliceClick}
          activeBottomTab={activeBottomTab}
          runDistribution={runDistribution}
          runOvertimeTrend={runOvertimeTrend}
          runOvertimeCards={runOvertimeCards}
          jobTypeByStatusData={jobTypeByStatusData}
          scheduleByHourData={scheduleByHourData}
          isRunDistributionLoading={isRunDistributionLoading}
          isRunTrendLoading={isRunTrendLoading}
          isRunTrendCardsLoading={isRunTrendCardsLoading}
          isJobTypeStatusLoading={isJobTypeStatusLoading}
          isScheduleByHourLoading={isScheduleByHourLoading}
        />
      )}
    </Box>
  );
};

const TableChartContent = (props: any) => {
  const { activeTile } = props;

  const isCustomStatusTile = CUSTOM_STATUS_TILE_SET.has(activeTile);
  const [resetKey, setResetKey] = useState(0);
  const prevTileRef = useRef<string>(activeTile);
  // Purge any implicit InProgress seed from the cache during render,
  // before children mount.
  useMemo(() => {
    const prevTile = prevTileRef.current;
    prevTileRef.current = activeTile;

    const wasCustom = CUSTOM_STATUS_TILE_SET.has(prevTile);
    if (isCustomStatusTile || !wasCustom) return;
    if (isCustomStatusTile) return;
    const cached = filterCache.getColumnFilterState<any>();
    const payload = Array.isArray(cached?.payload) ? cached.payload : [];
    const filteredPayload = payload.filter((f: any) => {
      if (f?.id !== 'execution_status') return true;
      const vals = f?.selOptions?.length ? f.selOptions : f?.values || [];
      const onlyInProgress =
        vals.length > 0 &&
        vals.every((s: any) =>
          ['inprogress', 'in progress'].includes(String(s).toLowerCase())
        );
      return !onlyInProgress;
    });
    if (filteredPayload.length !== payload.length) {
      const filteredModel = { ...(cached?.filterModel || {}) };
      const es = filteredModel.execution_status;
      const esVals = Array.isArray(es?.selOptions) ? es.selOptions : [];
      const onlyInProgressModel =
        esVals.length > 0 &&
        esVals.every((s: any) =>
          ['inprogress', 'in progress'].includes(String(s).toLowerCase())
        );
      if (onlyInProgressModel) delete filteredModel.execution_status;
      filterCache.saveColumnFilterState({
        filterModel: filteredModel,
        sortModel: cached?.sortModel ?? [],
        payload: filteredPayload,
      });
    }
  }, [activeTile, isCustomStatusTile]);

  const compositeKey = `${isCustomStatusTile ? 'custom' : 'tile'}-${activeTile}-${resetKey}`;

  return (
    <TableWithColumnFilters
      key={compositeKey}
      isCustomStatusTile={isCustomStatusTile}
      onRequestReset={() => setResetKey((prev) => prev + 1)}
      {...props}
    />
  );
};

const customFilterFields = [
  { label: 'Job Id', value: 'jobId' },
  { label: 'Job Name', value: 'jobName' },
  { label: 'Trigger Name', value: 'triggerName' },
  { label: 'Trigger Type', value: 'triggerType' },
  { label: 'Subscription Id', value: 'subscriptionId' },
  { label: 'Application Name', value: 'applicationName' },
  { label: 'Folder Name', value: 'folderName' },
];

const PipelineRunStatusDashboard = () => {
  const theme = useTheme();
  const p = theme.palette;
  const [controller] = usePepwiseAuthController();
  const { role } = controller;
  const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
  const { setRightContextContent } = useHeader();
  const navigate = useNavigate();

  const [activeContent, setActiveContent] = useState<'table' | 'chart'>('table');
  const [hoveredBottomTab, setHoveredBottomTab] = useState<number | null>(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [screenCapturing, setScreenCapturing] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [groupedData, setGroupedData] = useState<any[]>([]);
  const [isExportWarningOpen, setIsExportWarningOpen] = useState(false);
  const PIPELINE_BANNER_CONFIG = {
    dashboardUrl: '/dataOps/integratedJobMonitoring',
  } as const;
  const {
    banner: pipelineBanner,
    isVisible: isPipelineBannerVisible,
    dismiss: dismissPipelineBanner,
  } = useBanner(PIPELINE_BANNER_CONFIG);

  const {
    activeTile,
    activeBottomTab,
    setActiveBottomTab,
    page,
    pageSize,
    sortingInfo,
    appliedFilters,
    loadAllDropDowns,
    loadingFilterFields,
    summary,
    tableData,
    totalElements,
    isTotalCountLoading,
    isLoading,
    isExporting,
    filterOptions,
    setPage,
    setPageSize,
    setSortingInfo,
    handleTileClick,
    handleApplyFilters,
    getExportRecordCount,
    handleExport,
    isKpiLoading,
    isScheduledKpiLoading,
    isCustomStatusKpiLoading,
    scheduledSummary,
    customStatusSummary,
    runDistribution,
    runOvertimeTrend,
    runOvertimeCards,
    ianaTimezone,
    jobTypeByStatusData,
    scheduleByHourData,
    isRunDistributionLoading,
    isRunTrendLoading,
    isRunTrendCardsLoading,
    isJobTypeStatusLoading,
    isScheduleByHourLoading,
    dateRangeType,
    manualSaveFilters,
    resetToSavedFilters,
    hasSavedFilters,
    savedDefaultFilters,
    fetchPdfData,
    setGroupBy,
    groupBy ,
    setIsGrouped,
    loadMoreChildren,
    loadingGroupId,
    setLoadingGroupId,
    startDate,
    endDate,
    savedRangeType,
    timeZone,
    isScheduledForNextEnabled,
    dependentInfoLoading,
    setDependentInfoLoading,
    selectedAdditionalFilters,
    setSelectedAdditionalFilters,
    loadRunStatusKpi,  
    loadJobsList,
    isInitialFilterLoading,
    getColumnFilterValues,
    columnDetails,
  } = usePipelineRunStatus();
  
  const handleToggleAdditionalFilter = (filterId: string, checked: boolean) => {
    setSelectedAdditionalFilters(prev => {
      if (filterId === 'all') {
        const allFilterIds = ['scheduleCategory', 'scheduledForNext', 'sourceCategory', 'sourceType', 'sourceSubcategory', 'orchestratorScheduler', 'dataAssetName', 'department', 'capability', 'technology', 'subscriptionName', 'resourceGroup', 'resourceName', 'sustainOwner', 'sustainProdDate'];
        return checked ? allFilterIds : [];
      }
      return checked
        ? [...prev, filterId]
        : prev.filter(id => id !== filterId);
    });
  };

  const handleToggleAllAdditional = (checked: boolean) => {
    const allFilterIds = ['scheduleCategory', 'scheduledForNext', 'sourceCategory', 'sourceType', 'sourceSubcategory', 'orchestratorScheduler', 'dataAssetName', 'department', 'capability', 'technology', 'subscriptionName', 'resourceGroup', 'resourceName', 'sustainOwner', 'sustainProdDate'];
    setSelectedAdditionalFilters(checked ? allFilterIds : []);
  };

    // Helper function to check if custom date range is for today
  const isCustomDateRangeForToday = useMemo(() => {
    if (savedRangeType !== 'custom' || !startDate || !endDate) {
      return false;
    }
    const tzToUse = timeZone || 'CST';
    const ianaTimezone = TIMEZONE_MAP[tzToUse];
    const today = moment.tz(ianaTimezone).startOf('day');
    const selectedStart = moment(startDate).tz(ianaTimezone).startOf('day');
    const selectedEnd = moment(endDate).tz(ianaTimezone).startOf('day');
    
    return selectedStart.isSame(today) && selectedEnd.isSame(today);
  }, [savedRangeType, startDate, endDate, timeZone]);
  
  const isCurrentDateRange = useMemo(() => {
    if (!savedRangeType) return true;
    if (savedRangeType === 'custom') return isCustomDateRangeForToday;
    return NON_HISTORICAL_RANGE_TYPES.has(savedRangeType);
  }, [savedRangeType, isCustomDateRangeForToday]);

  let constRows: any = [];
  let setLocalData = false;

  const toggleJobDependency = async (name: number, resourceTypes: any, eleLevel: string, rowExpand: any) => {
  const rows = constRows.length ? constRows : tableData; // Use tableData if constRows is empty
    const rowIndex = rows.findIndex((r: any) => r.id === name);
    const row = rows[rowIndex];
    const newRows = [...rows];
    if (!rowExpand) {
      expandJobDependency({
        name,
        resourceTypes,
        row,
        rowIndex,
        newRows,
        startDate,
        endDate
      });
      return;
    }

    let children = row.lineageChildren ?? [];
    newRows.splice(Number(rowIndex) + 1, children.length);
    newRows[rowIndex] = { ...row, isLineageExpanded: false };
    const newUpdatedRows = [...newRows];

    constRows = newUpdatedRows;
    row['isLineageExpanded'] = false;
    setGroupedData(newUpdatedRows);  
};


  const expandJobDependency = async ({
    row,
    rowIndex,
    newRows,
    startDate,
    endDate,
    resourceTypes,
    name,
  }: ExpandLogicParams) => {
    const boxRows: any = localStorage.getItem('depJobExeData');
    const storageRows = boxRows ? JSON.parse(boxRows) : [];
    let hasStrRow = false;
    let children: Row[] = [];
    let childRows: Row[] = [];
    if (!setLocalData) {
      if (!hasStrRow) {
        let payload = tableData.filter((data: any) => data.id === name);
        let startDateLineage = moment.utc(payload[0].start_time).subtract(24, 'hours').format('YYYY-MM-DDTHH:mm:ssZ')
        let endDateLineage =  moment.utc(payload[0].start_time).format('YYYY-MM-DDTHH:mm:ssZ')
        let odate = payload[0].date;
        setDependentInfoLoading(true); 
        const res = await getDependentJobExeData({
          ...payload[0],
          startDate :startDateLineage ,
          endDate:endDateLineage,
          pipelineLineage:true,
          orderDate:odate 
        });
        setLocalData = true;
        setDependentInfoLoading(false);
        if (res.success) {
          res.data['dependInfo'] = res.data.depDataResult;

          Object.entries(res.data).forEach(([key, value]: any) => {
            childRows = dependInfoTransformData(value);
          });
          children = childRows.map((x: any) => {
            return { ...x, isLineageChildren: true, slaStatus: null };
          });
          newRows.splice(Number(rowIndex) + 1, 0, ...children);
          newRows[rowIndex] = { ...row, isLineageExpanded: true, lineageChildren: children };
          const newUpdatedRows = [...newRows];

          constRows = newUpdatedRows;
          setGroupedData(newUpdatedRows)
        }
      }
      if (resourceTypes?.length > 0) {
        childRows = resourceTypes;
      }
      children = childRows.map((x: any) => {
        return { ...x, isLineageChildren: true, slaStatus: null };
      });
      newRows.splice(Number(rowIndex) + 1, 0, ...children);
      newRows[rowIndex] = { ...row, isLineageExpanded: true, lineageChildren: children };
    }
  };

  const filterFields = [
    {
      label: 'Programs',
      placeholder: 'Select programs...',
      options: toOptions(filterOptions.program || []),
      value: appliedFilters.program || [],
      field: 'program',
      initialOptionLimit: 100,
    },
    {
      label: 'Product Short Name',
      placeholder: 'Select product short names...',
      options: toOptions(filterOptions.productShortName || []),
      value: appliedFilters.productShortName || [],
      field: 'productShortName',
    },
    {
      label: 'Domain',
      placeholder: 'Select domains...',
      options: toOptions(filterOptions.domain || []),
      value: appliedFilters.domain || [],
      field: 'domain',
    },
    {
      label: 'Sectors',
      placeholder: 'Select sectors...',
      options: toOptions(filterOptions.sector || []),
      value: appliedFilters.sector,
      field: 'sector',
    },
    {
      label: 'Miscellaneous Sectors',
      placeholder: 'Select misc sectors...',
      options: toOptions((filterOptions as any).miscellaneousSectors || []),
      value: appliedFilters.miscellaneousSectors || [],
      field: 'miscellaneousSectors',
  },
  {
      label: 'Market',
      placeholder: 'Select markets...',
      options: toOptions(filterOptions.market || []),
      value: appliedFilters.market,
      field: 'market',
      initialOptionLimit: 100,
    },
    {
      label: 'Job Type',
      placeholder: 'Select job types...',
      options: toOptions(filterOptions.jobType || []),
      value: appliedFilters.jobType,
      field: 'jobType',
    },
    {
      label: 'Business Unit',
      placeholder: 'Select business units...',
      options: toOptions(filterOptions.businessUnit || []),
      value: appliedFilters.businessUnit,
      field: 'businessUnit',
    },
    {
      label: 'Business Application',
      placeholder: 'Select business applications...',
      options: toOptions(filterOptions.appName || []),
      value: appliedFilters.appName,
      field: 'appName',
      initialOptionLimit: 100,
    },
    {
      label: 'Program Job Name',
      placeholder: 'Select program job names...',
      options: toOptions(filterOptions.program_job_name || []),
      value: appliedFilters.program_job_name,
      field: 'program_job_name',
      initialOptionLimit: 100,
    },
    {
      label: 'GDO',
      placeholder: 'Select gdo...',
      options: toOptions(filterOptions.gdo || []),
      value: appliedFilters.gdo,
      field: 'gdo',
      initialOptionLimit: 100,
    },
    {
      label: 'Schedule Category',
      placeholder: 'Select schedule categories...',
      options: toOptions(sortScheduleCategories(filterOptions.scheduleCategory || [])),
      value: appliedFilters.scheduleCategory,
      field: 'scheduleCategory',
    },
    {
      label: 'Scheduled for',
      placeholder: 'Select scheduled for...',
      options: getScheduledForNextOptions(),
      value: (() => {
        // Validate the saved value against current available options
        const availableOptions = getScheduledForNextOptions();
        const currentValue = appliedFilters.scheduledForNext;

        if (!currentValue || currentValue.length === 0) {
          return [];
        }

        // Filter to only include values that exist in current options
        const validValue = currentValue.filter(v =>
          availableOptions.some(opt => opt.value === v.value)
        );

        return validValue;
      })(),
      field: 'scheduledForNext',
      isSingleSelect: true,
      disabled: !isScheduledForNextEnabled,
    },
    {
      label: 'Source Environment',
      placeholder: 'Select source Environments...',
      options: toOptions(filterOptions.sourceCategory || []),
      value: appliedFilters.sourceCategory,
      field: 'sourceCategory',
    },
    {
      label: 'Source Sub Category',
      placeholder: 'Select source subcategories...',
      options: toOptions(filterOptions.sourceSubcategory || []),
      value: appliedFilters.sourceSubcategory || [],
      field: 'sourceSubcategory',
    },
    {
      label: 'Source',
      placeholder: 'Select sources...',
      options: toOptions(filterOptions.sourceType || []),
      value: appliedFilters.sourceType,
      field: 'sourceType',
    },
    {
      label: 'Orchestrator/Scheduler',
      placeholder: 'Select orchestrators/schedulers...',
      options: toOptions(filterOptions.orchestratorScheduler || []),
      value: appliedFilters.orchestratorScheduler,
      field: 'orchestratorScheduler',
    },
    {
      label: 'Business Stream',
      placeholder: 'Select business streams...',
      options: toOptions(filterOptions.businessStream || []),
      value: appliedFilters.businessStream,
      field: 'businessStream',
    },
    {
      label: 'Department',
      placeholder: 'Select departments...',
      options: toOptions(filterOptions.department || []),
      value: appliedFilters.department,
      field: 'department',
    },
    {
      label: 'Capability',
      placeholder: 'Select capabilities...',
      options: toOptions(filterOptions.capability || []),
      value: appliedFilters.capability,
      field: 'capability',
    },
    {
      label: 'Layer',
      placeholder: 'Select layers...',
      options: toOptions(filterOptions.layer || []),
      value: appliedFilters.layer,
      field: 'layer',
    },
    {
      label: 'Technology',
      placeholder: 'Select technologies...',
      options: toOptions(filterOptions.technology || []),
      value: appliedFilters.technology,
      field: 'technology',
    },
    {
      label: 'Data Asset Name',
      placeholder: 'Select data asset names...',
      options: toOptions(filterOptions.dataAssetName || []),
      value: appliedFilters.dataAssetName,
      field: 'dataAssetName',
      initialOptionLimit: 100,
    },
    {
      label: 'Bronze Table',
      placeholder: 'Select bronze tables...',
      options: toOptions(filterOptions.bronzeTableName || []),
      value: appliedFilters.bronzeTableName,
      field: 'bronzeTableName',
      initialOptionLimit: 100,
    },
    {
      label: 'Silver Table',
      placeholder: 'Select silver tables...',
      options: toOptions(filterOptions.silverTableName || []),
      value: appliedFilters.silverTableName,
      field: 'silverTableName',
      initialOptionLimit: 100,
    },
    {
      label: 'Gold Table',
      placeholder: 'Select gold tables...',
      options: toOptions(filterOptions.goldTableName || []),
      value: appliedFilters.goldTableName,
      field: 'goldTableName',
      initialOptionLimit: 100,
    },
    {
      label: 'Sustain Owner',
      placeholder: 'Select sustain owners...',
      options: toOptions(filterOptions.sustainOwner || []),
      value: appliedFilters.sustainOwner,
      field: 'sustainOwner',
    },
    {
      label: 'Sustain PROD Date',
      placeholder: 'Select sustain PROD dates...',
      options: toOptions(filterOptions.sustainProdDate || []),
      value: appliedFilters.sustainProdDate,
      field: 'sustainProdDate',
    },
    {
      label: 'Resource Name',
      placeholder: 'Select resource names...',
      options: toOptions(filterOptions.resourceName || []),
      value: appliedFilters.resourceName,
      field: 'resourceName',
      initialOptionLimit: 100,
    },
    {
      label: 'Resource Group',
      placeholder: 'Select resource groups...',
      options: toOptions(filterOptions.resourceGroup || []),
      value: appliedFilters.resourceGroup || [],
      field: 'resourceGroup',
      initialOptionLimit: 100,
    },
    {
      label: 'Subscription Name',
      placeholder: 'Select subscription names...',
      options: toOptions(filterOptions.subscriptionName || []),
      value: appliedFilters.subscriptionName || [],
      field: 'subscriptionName',
    },
      {
    label: 'Schedule Info',
    placeholder: 'Select schedule info...',
    options: toOptions(filterOptions.scheduleInfo || []),
    value: appliedFilters.scheduleInfo || [],
    field: 'scheduleInfo',
    initialOptionLimit: 100,
  },
  {
    label: 'Source Tables',
    placeholder: 'Select source tables...',
    options: toOptions(filterOptions.sourceTables || []),
    value: appliedFilters.sourceTables || [],
    field: 'sourceTables',
    initialOptionLimit: 100,
  },
  {
    label: 'PEC/Non PEC',
    placeholder: 'Select PEC/Non PEC...',
    options: toOptions(filterOptions.pecNonPec || []),
    value: appliedFilters.pecNonPec || [],
    field: 'pecNonPec',
    initialOptionLimit: 100,
  },
  {
    label: 'MEC/Non MEC',
    placeholder: 'Select MEC/Non MEC...',
    options: toOptions(filterOptions.mecNonMec || []),
    value: appliedFilters.mecNonMec || [],
    field: 'mecNonMec',
    initialOptionLimit: 100,
  },
  ].map((field) => ({
    ...field,
    loading: !!loadingFilterFields[field.field as keyof FilterState],
  }));

  const handleExportClick = async () => {
    const exportLimit = filterOptions?.exportLimit;   //Filer options updated from loadAllDropdown() apiData
    if (exportLimit) {
      const freshCount = await getExportRecordCount();
      if (freshCount > exportLimit) {
        setIsExportWarningOpen(true);
      } else {
        handleExport();
      }
    } else {
      handleExport();
    }
  };

  const filteredGroupByColumns = useMemo(() => {
    return includeGroupByMenuColumns;
  }, [activeTile, includeGroupByMenuColumns]);

  const handleGroupToggle = useCallback((row: any) => {
    setGroupedData(prevData => {
      // Find the index of the parent row
      const parentIndex = prevData.findIndex((r: any) =>
        r.isParent === true && r[groupBy] === row[groupBy]
      );

      if (parentIndex === -1) return prevData;

      const currentParent = prevData[parentIndex];

      // IMPORTANT: Get the actual total from the count field
      const countField = `${groupBy}Count`;
      const actualTotalChildren = currentParent[countField] || currentParent.totalChildrenCount || currentParent.children.length;

      const initialShowCount = 50;
      const hasMoreChildren = actualTotalChildren > initialShowCount;

      // Create a new array
      const newRows = [...prevData];

      if (!currentParent.isExpanded) {
        // EXPAND: Insert children after the parent
        const childrenToShow = currentParent.children.slice(0, initialShowCount);
        const childrenWithFlag = childrenToShow.map((child: any, idx: number) => ({
          ...child,
          isChildren: true,
          childIndex: idx,
          parentGroupValue: currentParent[groupBy]
        }));

        // Calculate insert position (right after the parent)
        const insertPosition = parentIndex + 1;

        // Insert children
        newRows.splice(insertPosition, 0, ...childrenWithFlag);

        if (hasMoreChildren) {
          const showMoreRow = {
            id: `show-more-${currentParent[groupBy]}`,
            isShowMoreRow: true,
            parentRow: {
              ...currentParent,
              totalChildrenCount: actualTotalChildren // ← CRITICAL: Update parentRow with actual total
            },
            currentShowCount: initialShowCount,
            totalChildrenCount: actualTotalChildren, // ← Use actual total here
            [groupBy]: currentParent[groupBy],
            groupBy: groupBy
          };
          newRows.splice(insertPosition + childrenToShow.length, 0, showMoreRow);
        }

        // Update parent state
        newRows[parentIndex] = {
          ...currentParent,
          isExpanded: true,
          showChildrenCount: initialShowCount,
          totalChildrenCount: actualTotalChildren // ← Also update parent's totalChildrenCount
        };
      } else {
        // COLLAPSE: Remove only the children and show more rows of this specific parent
        const showChildrenCount = currentParent.showChildrenCount || initialShowCount;
        let rowsToRemove = Math.min(showChildrenCount, actualTotalChildren);

        // Check if there's a show more row
        const hasShowMoreRow = hasMoreChildren && showChildrenCount < actualTotalChildren;
        if (hasShowMoreRow) {
          rowsToRemove += 1;
        }

        // Remove rows starting from position after parent
        newRows.splice(parentIndex + 1, rowsToRemove);

        // Update parent state
        newRows[parentIndex] = {
          ...currentParent,
          isExpanded: false,
          showChildrenCount: initialShowCount
        };
      }

      return newRows;
    });
  }, [groupBy]);


  
  const handleShowMoreClick = useCallback(async (showMoreRow: any) => {
    const { parentRow, currentShowCount, groupBy: groupByValue } = showMoreRow;

    // Use the groupBy from the showMoreRow or fallback to the parentRow's groupBy property
    const actualGroupByValue = groupByValue || groupBy;
    const groupValue = parentRow[actualGroupByValue];

    // Get the actual total from the count field
    const countField = `${actualGroupByValue}Count`;
    const actualTotalChildrenCount = parentRow[countField] || parentRow.totalChildrenCount;

    console.log("Actual total from count field:", actualTotalChildrenCount);
    console.log("Current show count:", currentShowCount);

    if (!actualGroupByValue || actualGroupByValue === 'none') {
      console.error('Invalid groupBy value:', actualGroupByValue);
      return;
    }

    if (!groupValue) {
      console.error('Invalid group value:', groupValue);
      return;
    }

    // Check if we have all children already loaded
    if (currentShowCount >= actualTotalChildrenCount) {
      console.log("All children already loaded");
      return;
    }

    // Set loading state for this specific group
    setLoadingGroupId(`${actualGroupByValue}-${groupValue}`);

    try {
      // Load next batch from API
      const result = await loadMoreChildren(actualGroupByValue, groupValue, currentShowCount, parentRow);

      if (result && result.children && result.children.length > 0) {
        setGroupedData(prevData => {
          const parentIndex = prevData.findIndex((r: any) =>
            r[actualGroupByValue] === groupValue && r.isParent === true
          );

          if (parentIndex === -1) return prevData;

          const currentParent = prevData[parentIndex];

          // Use the actual total from the parent
          const totalCount = currentParent[countField] || currentParent.totalChildrenCount;

          // Calculate new show count
          const newShowCount = Math.min(currentShowCount + result.children.length, totalCount);

          // Check if there are more children to load
          const hasMoreChildren = newShowCount < totalCount;

          console.log("Current show count:", currentShowCount);
          console.log("Loaded children count:", result.children.length);
          console.log("New show count:", newShowCount);
          console.log("Total children count:", totalCount);
          console.log("Has more children:", hasMoreChildren);

          // Find and remove the current "Show More" row
          const showMoreIndex = prevData.findIndex((r: any) =>
            r.id === showMoreRow.id
          );

          if (showMoreIndex === -1) return prevData;

          const newRows = [...prevData];

          // Remove the current "Show More" row
          newRows.splice(showMoreIndex, 1);

          // Insert new children at the correct position
          const insertPosition = parentIndex + 1 + currentShowCount;
          newRows.splice(insertPosition, 0, ...result.children);

          // Insert new "Show More" row if there are more children
          if (hasMoreChildren) {
            const newShowMoreRow = {
              id: `show-more-${groupValue}-${newShowCount}`,
              isShowMoreRow: true,
              parentRow: {
                ...currentParent,
                totalChildrenCount: totalCount
              },
              currentShowCount: newShowCount,
              totalChildrenCount: totalCount,
              [actualGroupByValue]: groupValue,
              groupBy: actualGroupByValue,
              isLoading: false
            };
            newRows.splice(insertPosition + result.children.length, 0, newShowMoreRow);
          }

          // Update parent's data - Merge the children array
          const updatedChildren = [...currentParent.children];
          result.children.forEach((newChild: any) => {
            const exists = updatedChildren.some(existingChild =>
              existingChild.job_id === newChild.job_id &&
              existingChild.start_time === newChild.start_time
            );
            if (!exists) {
              updatedChildren.push(newChild);
            }
          });

          // Update parent state
          newRows[parentIndex] = {
            ...currentParent,
            showChildrenCount: newShowCount,
            children: updatedChildren,
            isExpanded: true,
            totalChildrenCount: totalCount // Ensure parent has correct total
          };

          return newRows;
        });
      }
    } catch (error) {
      console.error('Error loading more children:', error);
    } finally {
      setLoadingGroupId(null);
    }
  }, [loadMoreChildren, groupBy]);



  // In PipelineRunStatusDashboard component
  const getColumnsWithGrouping = useCallback((baseColumns: any[], groupByValue: string) => {
    if (!groupByValue || groupByValue === 'none') {
      return baseColumns;
    }

    const groupedColumnIndex = baseColumns.findIndex(col => col.key === groupByValue);
    const groupedColumn = groupedColumnIndex !== -1 ? baseColumns[groupedColumnIndex] : null;

    const groupedFirstColumn = {
      key: groupByValue,
      name: groupedColumn ? groupedColumn.name : groupByValue.toUpperCase().replace(/_/g, ' '),
      minWidth: 250,  // Minimum width
      maxWidth: 750,
      width:500,
      formatter: ({ row }: any) => {
        if (row.isShowMoreRow) {
          const groupValue = row.parentRow[groupByValue];
          const isLoading = loadingGroupId === `${groupByValue}-${groupValue}`;
          return (
            <GroupByShowMore
              row={row}
              handleShowMoreClick={handleShowMoreClick}
              isLoading={isLoading}
            />
          );
        }

        return (
          <GroupedTableFormatter
            row={row}
            groupByColumn={groupByValue}
            groupByLabel={row.job_id || row.job_name || 'Unassigned'}
            onToggle={handleGroupToggle}
            countLabel={'Jobs'}
          />
        );
      },
      resizable: true,
      flexGrow: 2,
      sortable: true
    };

    let remainingColumns = [...baseColumns];
    if (groupedColumnIndex !== -1) {
      remainingColumns = baseColumns.filter((_, index) => index !== groupedColumnIndex);
    }

    const newColumns = [groupedFirstColumn, ...remainingColumns];

    return newColumns;
  }, [handleGroupToggle, handleShowMoreClick, loadingGroupId]);



  const tableColumns = useMemo(() => {
    const baseColumns = columns(ianaTimezone, activeTile, toggleJobDependency, columnDetails);

    if (groupBy && groupBy !== 'none') {
      const groupedColumns = getColumnsWithGrouping(baseColumns, groupBy);
      console.log('Columns after grouping:', groupedColumns.map(col => col.key));
      return groupedColumns;
    }

    return baseColumns;
  }, [ianaTimezone, activeTile, toggleJobDependency, groupBy, getColumnsWithGrouping]);

console.log("main index loading",isLoading)

  // In the useEffect that sets groupedData from tableData
  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const isAlreadyGrouped = tableData[0]?.isParent === true || Array.isArray(tableData[0]?.children);

      if (isAlreadyGrouped) {
        const resetGroupedData = tableData.map((group: any) => ({
          ...group,
          isExpanded: false,
          showChildrenCount: 50,
          // Store the actual total children count from the API response if available
          // This should come from the API response in the group object
          totalChildrenCount: group.totalCount || group.children?.length || 0
        }));
        setIsGrouped(true);
        setGroupedData(resetGroupedData);
      } else {
        setIsGrouped(false);
        setGroupedData(tableData);
      }
    } else {
      setGroupedData([]);
      setIsGrouped(false);
    }
}, [tableData, groupBy,loadMoreChildren]);

  // Validate scheduledForNext filter against available options
  // If saved value is no longer available, clear it
  useEffect(() => {
    const hasScheduledforNext = 
    appliedFilters.scheduledForNext &&
    appliedFilters.scheduledForNext.length > 0;

    if (!hasScheduledforNext) return;

    if (!isScheduledForNextEnabled) {
      handleApplyFilters({
        ...appliedFilters,
        scheduledForNext: [],
      });
      return;
    }

    const availableOptions = getScheduledForNextOptions();
    const currentValue = appliedFilters.scheduledForNext[0];

    const isCurrentValueValid = availableOptions.some(
      opt => opt.value === currentValue.value
    );

    if (!isCurrentValueValid) {
      handleApplyFilters({
        ...appliedFilters,
        scheduledForNext: [],
      });
    }
  }, [isScheduledForNextEnabled, appliedFilters, handleApplyFilters]);

  const dateRangePrefix = dateRangeType !== 'Today' ? 'the' : '';
  const baseTooltip = `over ${dateRangePrefix} ${dateRangeType}`;
  const tooltipContent = (tileKey: string) => {
    if (tileKey === 'Scheduled') {
      return `Schedule uses job metadata and FS61`;
    }
    if (CUSTOM_STATUS_TOOLTIP_TEXT[tileKey]) {
      return CUSTOM_STATUS_TOOLTIP_TEXT[tileKey];
    }
    return baseTooltip;
  };
  const tableTitle = getTileLabel(activeTile);

  const handleApplyFiltersOnly = (filters: FilterState) => {
    handleApplyFilters(filters);
  };

  const handleSaveAsDefault = async (filters: FilterState) => {
    handleApplyFilters(filters);
    const result = await manualSaveFilters(filters);
    if (result?.success) {
      console.log('Filters saved as default successfully');
    }
  };

  const handleResetToDefault = async () => {
  const result = await resetToSavedFilters();
  
  if (result?.success) {
    const resetFilters = result.filters;
    
    if (resetFilters) {
      filterCache.clearAll();
      
      setPage(1);
      
      handleApplyFilters(resetFilters, true); 
      
      const fieldsToLoad = Object.keys(resetFilters).filter(
        key => {
          const value = resetFilters[key as keyof FilterState];
          return Array.isArray(value) && value.length > 0;
        }
      );
      
      setTimeout(() => {
        fieldsToLoad.forEach(field => {
          loadAllDropDowns(true, resetFilters, field as keyof FilterState);
        });
      }, 100);
      
      setTimeout(async () => {
        await loadRunStatusKpi();
        await loadJobsList();
      
      }, 200);
    }
  }
};

  const transformedSummary = useMemo(() => {
    const base = summary ? transformSummaryData(summary) : null;
    const scheduled = scheduledSummary ? transformSummaryData(scheduledSummary) : null;
    const customStatus = customStatusSummary ? transformSummaryData(customStatusSummary) : null;
    
    if (!base && !scheduled && !customStatus) return null;
    if (!base) return scheduled;
    if (!scheduled) return base;

    // Read all the KPIs/widgets count data from API and pass it to the respective widgets
    return {
      ...base,
      scheduled:
        scheduled?.scheduled ?? {
          total: 0,
          difference: 0,
          trend: "NO_CHANGE",
        },
      hung: customStatus?.hung ?? {
        total: 0,
        difference: 0,
        trend: "NO_CHANGE"
      },
      longRunning: customStatus?.longRunning ?? {
        total: 0,
        difference: 0,
        trend: "NO_CHANGE"
      }
    };
  }, [summary, scheduledSummary, customStatusSummary]);

  const handleScreenCapture = async () => {
    try {
      setScreenCapturing(true);

      const element = document.getElementById('prs-capture-area');
      if (!element) return;

      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        ignoreElements: (el: any) => ['exclude-camera', 'exclude-export'].includes(el.id)
      });

      const image = canvas.toDataURL('image/png');

      setPreviewImage(image);
      setOpenPreview(true);
    } catch (err) {
      console.error('Screenshot failed', err);
    } finally {
      setScreenCapturing(false);
    }
  };

  const handleSourceSliceClick = (source: string) => {
    setActiveContent("table");
    handleTileClick('All');
    handleApplyFilters({
      ...appliedFilters,
      sourceType: [{ label: source, value: source }]
    });
  };

  const handleDownloadPDF = async () => {
    if (isPdfGenerating) return;
    setIsPdfGenerating(true);
    try {
      const pdfData = await fetchPdfData();
      const isScheduled = activeTile === 'Scheduled';

      // ── Time formatter helper ──────────────────────────────────
      const fmtTime = (field: string) => (row: any): string => {
        const v = row?.[field];
        if (!v) return '--';
        try {
          const d = new Date(v);
          const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: ianaTimezone, year: 'numeric', month: '2-digit', day: '2-digit',
              }).formatToParts(d);
          const yr = parts.find(p => p.type === 'year')?.value;
          const mo = parts.find(p => p.type === 'month')?.value;
          const dy = parts.find(p => p.type === 'day')?.value;
          const time = d.toLocaleTimeString('en-US', {
            timeZone: ianaTimezone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
          });
          return `${mo}-${dy}-${yr} ${time}`;
        } catch { return String(v); }
      };

      const fmtMs = (field: string) => (row: any): string => {
        const n = Number(row?.[field]);
        return (row?.[field] != null && !isNaN(n)) ? (n / 60000).toFixed(2) : '--';
      };

      const fmtStatus = (row: any): string => {
        const s = String(row?.execution_status ?? '').toLowerCase();
          if (['abort','aborted'].includes(s)) return 'ABORTED';
        if (['success','successful','succeeded'].includes(s)) return 'SUCCESSFUL';
        if (['fail','failed'].includes(s)) return 'FAILED';
        if (['inprogress','in progress'].includes(s)) return 'IN PROGRESS';
        if (s === 'delayed') return 'DELAYED';
        if (s === 'scheduled') return 'SCHEDULED';
        return (row?.execution_status ?? '--').toString().toUpperCase();
      };

      const fmtDate = (row: any): string => {
        const v = row?.date;
        if (!v) return '--';
        const [yyyy, mm, dd] = String(v).split('-');
        return `${mm}-${dd}-${yyyy}`;
      };

      const fmtRecurrence = (row: any): string => {
        if (row?.is_recurrence === true) return 'Yes';
        if (row?.is_recurrence === false) return 'No';
        return '--';
      };

      // ── All possible PDF column definitions, keyed by data field ──
      const allPdfColumnsMap: Record<string, PdfColumn> = {
        'date':              { key: 'date',              label: 'DATE',              weight: 14, formatter: fmtDate },
        'hour':              { key: 'hour',              label: 'HOUR',              weight: 8,  formatter: r => r?.hour != null && !isNaN(r.hour) ? String(Number(r.hour)) : '--' },
        'job_type':          { key: 'job_type',          label: 'JOB TYPE',          weight: 15, formatter: r => r?.job_type || '--' },
        'job_id':            { key: 'job_id',            label: 'JOB ID',            weight: 18, formatter: r => r?.job_id || '--' },
        'run_id':            { key: 'run_id',            label: 'JOB RUN URL',            weight: 22, 
          isLink: true,
          linkFormatter: r => {
            const isAzureSource = r?.source_type === 'ADF' || r?.source_type === 'ADB';
            if (!isAzureSource) return null;
            return r?.run_url || null;
          },
          formatter: r => {
            const isAzureSource = r?.source_type === 'ADF' || r?.source_type === 'ADB';
            if (!isAzureSource) return '-';
            return r?.run_url || '--';
        }},
        'job_name':          { key: 'job_name',          label: 'JOB NAME',          weight: 26, formatter: r => r?.job_name || '--' },
        'execution_status':  { key: 'execution_status',  label: 'STATUS',            weight: 15, formatter: fmtStatus, isStatus: true },
        'start_time':        { key: 'start_time',        label: isScheduled ? 'SCHEDULED TIME' : 'START TIME', weight: 22, formatter: fmtTime('start_time') },
        'end_time':          { key: 'end_time',          label: 'END TIME',          weight: 22, formatter: fmtTime('end_time') },
        'source_type':       { key: 'source_type',       label: 'SOURCE',            weight: 15, formatter: r => r?.source_type || '--' },
        'program_name':      { key: 'program_name',      label: 'PROGRAM',           weight: 18, formatter: r => r?.program_name || '--' },
        'duration_ms':       { key: 'duration_ms',       label: 'RUN TIME MIN',      weight: 14, formatter: fmtMs('duration_ms') },
        'estimated_end_time':{ key: 'estimated_end_time', label: 'EXPECTED END TIME', weight: 22, formatter: fmtTime('estimated_end_time') },
        'avg_duration_ms':   { key: 'avg_duration_ms',   label: 'AVG RUN TIME MIN',  weight: 13, formatter: fmtMs('avg_duration_ms') },
        'total_exe_count':   { key: 'total_exe_count',   label: 'TOTAL RECORDS',     weight: 13, formatter: r => r?.total_exe_count != null && !isNaN(r.total_exe_count) ? Number(r.total_exe_count).toLocaleString() : '--' },
        'is_recurrence':     { key: 'is_recurrence',     label: 'RECURRING',         weight: 11, formatter: fmtRecurrence },
        'recurrence_per_day':{ key: 'recurrence_per_day', label: 'RECURRENCES PER DAY', weight: 11, formatter: r => r?.recurrence_per_day != null && !isNaN(r.recurrence_per_day) ? Number(r.recurrence_per_day).toLocaleString() : '--' },
        'trigger_name':      { key: 'trigger_name',      label: 'TRIGGER NAME',       weight: 18, formatter: r => r?.source_type === 'ADF' ? (r?.trigger_name || '--') : '--' },
        'trigger_type':      { key: 'trigger_type',      label: 'TRIGGER TYPE',       weight: 15, formatter: r => r?.source_type === 'ADF' ? (r?.trigger_type || '--') : '--' },
        'data_asset_name':   { key: 'data_asset_name',   label: 'DATA ASSET NAME',    weight: 18, formatter: r => r?.source_type === 'ADF' ? (r?.data_asset_name || '--') : '--' },
        'silver_table_name': { key: 'silver_table_name', label: 'SILVER TABLE NAME',  weight: 18, formatter: r => r?.source_type === 'ADF' ? (r?.silver_table_name || '--') : '--' },
        'bronze_table_name': { key: 'bronze_table_name', label: 'BRONZE TABLE NAME',  weight: 18, formatter: r => r?.source_type === 'ADF' ? (r?.bronze_table_name || '--') : '--' },
         'sector':           { key: 'sector',            label: 'SECTOR',             weight: 15, formatter: r => r?.sector || '--' },
          'subscription_name':           { key: 'subscription_name',            label: 'SUBSCRIPTION NAME',             weight: 15, formatter: r => r?.subscription_name || '--' },
          'subscription_id':           { key: 'subscription_id',            label: 'SUBSCRIPTION ID',             weight: 15, formatter: r => r?.subscription_id || '--' },
          'resource_group':           { key: 'resource_group',            label: 'RESOURCE GROUP',             weight: 15, formatter: r => r?.resource_group || '--' },
          'resource_name':           { key: 'resource_name',            label: 'RESOURCE NAME',             weight: 15, formatter: r => r?.resource_name || '--' },
            'job_no':           { key: 'job_no',            label: 'JOB NUMBER',             weight: 15, formatter: r => r?.job_no || '--' },
          'application_name':           { key: 'application_name',            label: 'APPLICATION NAME',             weight: 15, formatter: r => r?.application_name || '--' },
          'folder_name':           { key: 'folder_name',            label: 'FOLDER NAME',             weight: 15, formatter: r => r?.folder_name || '--' },
          
      };


      // ── Build name→key mapping from table column definitions ──
      const tableColDefs = columns(ianaTimezone, activeTile, toggleJobDependency);
      const nameToKeyMap: Record<string, string> = {};
      tableColDefs.forEach((col: any) => {
        nameToKeyMap[col.name] = col.key;
      });

      // ── Read user's column preferences from localStorage ──
      const tableType = `pipeline-run-status${activeTile}`;
      const storedPref = localStorage.getItem(`${tableType}_user_col_pref`);
      let preferredColumnNames: string[] | null = null;
      if (storedPref && storedPref !== 'undefined') {
        try {
          preferredColumnNames = JSON.parse(storedPref);
        } catch { preferredColumnNames = null; }
      }

      // Fall back to default columns if no user preference is stored
      const columnNames = (preferredColumnNames && Array.isArray(preferredColumnNames) && preferredColumnNames.length > 0)
        ? preferredColumnNames
        : getDefaultColumns(activeTile);

      // ── Build PDF columns in user's preferred order ────────────
      const pdfColumns: PdfColumn[] = columnNames
        .map(name => {
          const dataKey = nameToKeyMap[name];
          if (!dataKey) return undefined;
          const pdfCol = allPdfColumnsMap[dataKey];
          if (!pdfCol) return undefined;
          return { ...pdfCol, label: name };
        })
        .filter((col): col is PdfColumn => col !== undefined);

      await generateDashboardPdf({
        columns:            pdfColumns,
        allData:            pdfData,
        dashboardTitle:     'Integrated Job Monitoring Dashboard',
        tableTitle,
       totalElements,
        topKpiElementId:    'ua-top-tabs',
        fileNamePrefix:     `IntegratedJobMonitoring_${tableTitle}`,
      });
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleSummaryTileClick = (key: string) => {
    handleTileClick(key);
    setActiveContent('table');
  };

  useEffect(() => {
    setRightContextContent(
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton
          onClick={() => setIsFilterDrawerOpen(true)}
          sx={{ position: 'relative', p: '0px !important' }}
        >
          {<FilterIcon />}
        </IconButton>
      </Box>
    );

    return () => {
      setRightContextContent(null);
    };
  }, [
    setRightContextContent
  ]);

  const headerOffset = { xs: '87px', sm: '67px', md: '77px' }; 

  return (
    <>
    {isPipelineBannerVisible && (
      <Box
        sx={{
          mt: headerOffset,
          width: '100%',
          '& .MuiPaper-root': {
            width: '100%',
            margin: '0 !important',
            boxSizing: 'border-box',
            borderRadius: 0,
          },
        }}
      >
        <Banner
          severity={(pipelineBanner?.severity || pipelineBanner?.contentType as any) || 'info'}
          title={pipelineBanner?.title}
          message={pipelineBanner?.text ?? ''}
          onDismiss={dismissPipelineBanner}
        />
      </Box>
    )}

    <PageWrapper sx={{ ...(isPipelineBannerVisible ? { mt: 0, pt: {xs: 2, md: 2, lg: 2} } : {}), minHeight: 'calc(100vh - 120px)', backgroundColor: p.backgroundLight.sideNavBgHover, position: 'relative' }}>
      <GlobalStyles styles={{ '#exclude-export': { position: 'relative !important' } }} />

      <Box id="ua-main" sx={{ pt: isPipelineBannerVisible ? 0 : 1, position: 'relative', backgroundColor: p.backgroundLight.sideNavBgHover }}>
        <Box id="prs-capture-area">
          <Box id="ua-top-tabs" sx={{ display: 'flex', alignItems: 'stretch', gap: 1, width: '100%' }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex'
              }}
            >
              <SummaryTiles
                summary={transformedSummary}
                gridColumns={{ xs: 1, sm: 2, md: 2, lg: 4 }}
                activeTile={activeContent === 'chart' ? '' : activeTile}
                onTileClick={(key) => { handleSummaryTileClick(key); }}
                isLoading={isKpiLoading}
                isScheduledLoading={isScheduledKpiLoading}
                isCustomStatusLoading={isCustomStatusKpiLoading}
                tilesConfig={TILES_CONFIG}
                tooltipLabel={tooltipContent}
                tileLabel={(tileKey: string) => {
                  if (tileKey === 'Scheduled') {
                    const selected = appliedFilters.scheduledForNext?.[0];
                    const selectedLabel = selected ? selected.label : '';
                    return selectedLabel.replace(/\bhr\b/i, 'Hr');
                  }
                  if (tileKey === STATUS_FILTER_LABELS.SHORT_RUN) return '';
                  if (CUSTOM_STATUS_OPTIONS.includes(tileKey)) return '';
                  return 'From Last 1 Hr';
                }}
                showTrend={isCurrentDateRange} 
              />
            </Box>
            {(!isLoading) &&
              <CaptureExportControls
                onScreenshot={handleScreenCapture}
                screenCapturing={screenCapturing}
                onExport={handleExportClick}
                isExporting={isExporting}
                isVisible={true}
                showExport={activeContent === 'table'}
                type="pipeline"
                exportTooltip="Max 200K records will be exported at a Time."
              />}
          </Box>

          <Box sx={{ position: 'relative' }}>
            {isLoading && (
              <Box
                id="ua-tab-content-loading"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 5,
                  backgroundColor: p.white.main,
                  p: 3,
                  pt: activeContent === 'chart' ? 5 : 3,
                  minHeight: '250px',
                }}
              >
                <Typography variant="h4" sx={{ color: p.primary.main, fontWeight: 500 }}>
                  <HighlightedText text={tableTitle} />
                </Typography>
                <LoaderComponent size={50} py={10} />
              </Box>
            )}
            <TableChartContent
              p={p}
              activeContent={activeContent}
              activeTile={activeTile}
              totalElements={totalElements}
              isTotalCountLoading={isTotalCountLoading}
              page={page}
              pageSize={pageSize}
              sortingInfo={sortingInfo}
              setPage={setPage}
              setPageSize={setPageSize}
              setSortingInfo={setSortingInfo}
              handleSourceSliceClick={handleSourceSliceClick}
              activeBottomTab={activeBottomTab}
              runDistribution={runDistribution}
              runOvertimeTrend={runOvertimeTrend}
              runOvertimeCards={runOvertimeCards}
              jobTypeByStatusData={jobTypeByStatusData}
              scheduleByHourData={scheduleByHourData}
              isRunDistributionLoading={isRunDistributionLoading}
              isRunTrendLoading={isRunTrendLoading}
              isRunTrendCardsLoading={isRunTrendCardsLoading}
              isJobTypeStatusLoading={isJobTypeStatusLoading}
              isScheduleByHourLoading={isScheduleByHourLoading}
            tableTitle = {tableTitle}
              setGroupBy={setGroupBy}
              displayData={groupedData}
              groupByValue={groupBy}
              tableColumns={tableColumns}
              includeGroupByMenuColumns={filteredGroupByColumns}
              onMetadataUpdatesClick={isAllowed ? () => navigate('/dataOps/integratedJobMonitoring/metadataUpdates') : undefined}
              dependentInfoLoading={dependentInfoLoading}
              setDependentInfoLoading={setDependentInfoLoading}
              loadJobsList={loadJobsList}
              getColumnFilterValues={getColumnFilterValues}
              isPrevNextPagination={true}
            />
          </Box>
          <Box id="ua-bottom-tabs"
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              width: {
                xs: '100%',
                md: '66.67%',
              },
            }}>
            {CHART_CARDS.map((card) => {
              const isActive = activeContent === 'chart' && activeBottomTab === card.key;
              const isHovered = hoveredBottomTab === card.key;
              const { Icon } = card;
              const iconColor = isActive || isHovered ? p.primary.main : p.white.main;
              const sub = card.label === 'Job Type By Status' && jobTypeByStatusData ? `View ${jobTypeByStatusData?.tableData.length}` : card.sub;

              return (
                <Box
                  key={card.key}
                  onClick={() => { setActiveBottomTab(card.key); setActiveContent('chart'); }}
                  onMouseEnter={() => setHoveredBottomTab(card.key)}
                  onMouseLeave={() => setHoveredBottomTab(null)}
                  sx={{
                    flex: {
                      xs: '1 1 100%',
                      sm: '1',
                      md: '1',
                    },

                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    py: { xs: 1.5, md: 2.5 },
                    px: { xs: 1, md: 2 },
                    cursor: 'pointer',
                    backgroundColor: isActive || isHovered ? p.white.main : p.primary.main,
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                    <Icon color={iconColor} />
                  </Box>
                  <Typography variant="body1" sx={{ color: iconColor, fontWeight: 500, textAlign: 'right', lineHeight: 1.2 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: iconColor, textAlign: 'right' }}>
                    {sub}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <ImagePreview
        open={openPreview}
        image={previewImage}
        fileName={`IntegratedJobMonitoring_${tableTitle}_${new Date().toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replaceAll(/[/, ]/g, '_')}`}
        onClose={() => setOpenPreview(false)}
        title={'Integrated Job Monitoring Dashboard'}
        hasDownloadPDFButton={activeContent === 'table'}
        handleDownloadPDF={handleDownloadPDF}
        isPdfGenerating={isPdfGenerating}
      />

      <ExportLimitWarningDialog
        isOpen={isExportWarningOpen}
        limit={filterOptions?.exportLimit || 500}
        handleClose={() => setIsExportWarningOpen(false)}
        handleConfirm={handleExport}
      />

        <FilterDrawer
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFiltersOnly}
        onSaveAsDefault={handleSaveAsDefault}
        onResetToDefault={handleResetToDefault}
        hasSavedFilters={hasSavedFilters}
        savedDefaultFilters={savedDefaultFilters}
        currentFilters={{ ...appliedFilters, miscellaneousSectors: appliedFilters.miscellaneousSectors || [] }}
        filterFields={filterFields}
        customFilterFields={customFilterFields}
        onDropdownOpen={(draftFilters: FilterState, field: keyof FilterState) => loadAllDropDowns(false, draftFilters, field)}
        additionalFilterOptions={filterFields
          .filter(f => !['program', 'sector', 'market', 'jobType', 'businessUnit', 'appName', 'miscellaneousSectors'].includes(f.field as string))
          .map(f => ({
            id: f.field as string,
            label: f.label,
            level: (['sourceSubcategory', 'orchestratorScheduler', 'goldTableName'].includes(f.field as string)) ? 2 : 1
          }))
        }
        selectedAdditionalFilters={selectedAdditionalFilters}// You can manage this state in your parent if needed
        onToggleAdditionalFilter={handleToggleAdditionalFilter}
        onToggleAllAdditional={handleToggleAllAdditional}
        isInitialLoading={isInitialFilterLoading}

      />
    </PageWrapper>
    </>
  );
};

export default PipelineRunStatusDashboard;